import React, { useState } from "react";
import {
  Paper,
  Stack,
  Box,
  Typography,
  Collapse,
  Button,
  alpha,
  Tooltip,
  IconButton,
  useTheme,
  Divider,
  Chip,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import FullscreenDialog from "../../../../utils/dialog/FullscreenDialog";
import ExpandedArrowBox from "../../../../Common/ExpandedArrowBox";

const SectionBox = ({
  children,
  title,
  titleChip,
  caType,
  scheduleNo,
  subtitle,
  icon = <ReceiptLongIcon color='primary' />, // Allow custom icon
  onAdd,
  totalAmount,
  totalAmountColor,
  showAddButton = false,
  addButtonLabel = "Add Row",
  id,
  expanded: expandedProp,
  onToggleExpand,
  headerActions,
  contentSx = {},
  addButtonInHeader = false,
  isFullscreen,
  onFullscreenChange,
  canEdit = true,
  ...paperProps
}) => {
  const theme = useTheme();
  const [internalExpanded, setInternalExpanded] = useState(true);
  const expanded = expandedProp !== undefined ? expandedProp : internalExpanded;
  const handleToggle = () => {
    if (onToggleExpand) onToggleExpand(!expanded);
    else setInternalExpanded((prev) => !prev);
  };
  const [isExpandedDialogOpen, setIsExpandedDialogOpen] = useState(false);
  // Use controlled fullscreen if provided, else fallback to internal state
  const dialogOpen =
    isFullscreen !== undefined ? isFullscreen : isExpandedDialogOpen;
  const handleDialogOpen = (e) => {
    e.stopPropagation();
    if (onFullscreenChange) onFullscreenChange(true);
    else setIsExpandedDialogOpen(true);
  };
  const handleDialogClose = () => {
    if (onFullscreenChange) onFullscreenChange(false);
    else setIsExpandedDialogOpen(false);
  };

  return (
    <>
      <Paper
        id={id}
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 4,
          border: "1px solid",
          borderColor: "divider",
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
          background: theme.palette.background.paper,
          "&:hover": {
            borderColor: alpha(theme.palette.primary.main, 0.2),
            boxShadow: `0 2px 10px ${alpha(
              theme.palette.common.black,
              0.05
            )}`,
          },
        }}
        {...paperProps}
      >
        {/* Enhanced Header */}
        <Stack
          direction='row'
          alignItems='center'
          spacing={2}
          sx={{
            px: 3,
            py: 2,
            borderTopLeftRadius: "inherit",
            borderTopRightRadius: "inherit",
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.04
            )}, ${alpha(theme.palette.primary.main, 0.02)})`,
            borderBottom: "1px solid",
            borderColor: alpha(theme.palette.primary.main, 0.1),
            cursor: "pointer",
            position: "relative",
            "&:hover": {
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.06
              )}, ${alpha(theme.palette.primary.main, 0.03)})`,
            },
          }}
          onClick={handleToggle}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 2.5,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.1
              )}, ${alpha(theme.palette.primary.main, 0.05)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            }}
          >
            {icon}
          </Box>
          <Box flex={1} sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                columnGap: 1.25,
                rowGap: 0.5,
                flex: 1,
              }}
            >
              <Typography
                variant='h6'
                fontWeight={700}
                color='text.primary'
                sx={{
                  fontSize: "1.1rem",
                  letterSpacing: "0.2px",
                  overflowWrap: "anywhere",
                }}
              >
                {title}
              </Typography>
              {titleChip && (
                <Chip
                  label={titleChip}
                  size='small'
                  sx={{
                    height: 26,
                    px: 0.5,
                    borderRadius: 2,
                    fontWeight: 500,
                    background: `linear-gradient(135deg, ${alpha(
                      theme.palette.primary.main,
                      0.1
                    )}, ${alpha(theme.palette.primary.main, 0.05)})`,
                    border: `1px solid ${alpha(
                      theme.palette.primary.main,
                      0.12
                    )}`,
                    "& .MuiChip-label": {
                      px: 1,
                      fontWeight: 600,
                    },
                  }}
                />
              )}
            </Box>

            {subtitle && (
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{
                  mt: 0.5,
                  fontWeight: 400,
                  lineHeight: 1.5,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          {totalAmount !== undefined && (
            <Typography
              variant='h6'
              fontWeight={700}
              color={totalAmountColor || "text.primary"}
              sx={{
                minWidth: 120,
                textAlign: "right",
                fontSize: "1.1rem",
              }}
            >
              {totalAmount}
            </Typography>
          )}
          {headerActions}

          {addButtonInHeader && showAddButton && canEdit && (
            <Tooltip title={addButtonLabel}>
              <IconButton
                color='primary'
                onClick={(e) => {
                  e.stopPropagation();
                  onAdd && onAdd();
                }}
                sx={{
                  flexShrink: 0,
                  transition: "background-color 0.2s ease",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </Tooltip>
          )}

          <ExpandedArrowBox expanded={expanded} />
        </Stack>

        <Collapse in={expanded}>
          <Box sx={{ p: 3, ...contentSx }}>
            {children}
            {!addButtonInHeader && showAddButton && canEdit && (
              <Stack
                direction='row'
                justifyContent='flex-end'
                spacing={2}
                sx={{
                  mt: 3,
                  pt: 2,
                  borderTop: "1px solid",
                  borderColor: alpha(theme.palette.divider, 0.1),
                }}
              >
                <Button
                  onClick={onAdd}
                  variant='outlined'
                  color='primary'
                  startIcon={<AddCircleOutlineIcon />}
                  sx={{
                    borderRadius: 3,
                    textTransform: "none",
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    transition: "background-color 0.2s ease, border-color 0.2s ease",
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.06),
                    },
                  }}
                >
                  {addButtonLabel}
                </Button>
              </Stack>
            )}
          </Box>
        </Collapse>

        <Divider />
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          sx={{ p: 2 }}
        >
          <Typography variant='body2' color='text.secondary'>
            {caType} Schedule
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Sch {scheduleNo}
          </Typography>
        </Stack>
      </Paper>

      {/* Fullscreen Expand Dialog */}
      <FullscreenDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        title={title}
        subtitle={subtitle}
        addButtonInHeader={addButtonInHeader}
        showAddButton={showAddButton}
        addButtonLabel={addButtonLabel}
        onAdd={onAdd}
      >
        {children}
      </FullscreenDialog>
    </>
  );
};

export default SectionBox;
